import React, { useState, useEffect, useCallback } from 'react';
import CheckboxField from './CheckboxField';
import RangeField from './RangeField';
import IconsField from './IconsField';
import DatabaseProduct from './DatabaseProduct';
import FeaturesField from './FeaturesField';

const generateUniqueId = () => '_' + Math.random().toString(36).substr(2, 9);

const parseQueryParams = (query) => {
  const params = new URLSearchParams(query);
  const filters = {};
  for (const [key, value] of params.entries()) {
    if (key === 'price') {
      filters[key] = JSON.parse(decodeURIComponent(value));
    } else {
      filters[key] = value.split(',');
    }
  }
  return filters;
};

const DatabaseComponent = ({ data }) => {
  const initialGlobalFilter = parseQueryParams(window.location.search);

  const [fieldsWithIds, setFieldsWithIds] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(initialGlobalFilter);
  const [selectedOptions, setSelectedOptions] = useState(initialGlobalFilter);
  const [rangeValues, setRangeValues] = useState();
  const [initialRangeValues, setInitialRangeValues] = useState({});
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [sorting, setSorting] = useState('our-top-picks');
  const [productCount, setProductCount] = useState(0);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const productType = data.product_type;
  const displayFields = data.display_fields;

  useEffect(() => {
    const fields = data.fields.map(field => {
      if (field.type === 'range' && field.field == 'price' && initialGlobalFilter['price']) {
        const range = {
          min: field.fields_range.min,
          max: field.fields_range.max,
          start: initialGlobalFilter['price'].min,
          end: initialGlobalFilter['price'].max,
        }
        return { ...field, uniqueId: generateUniqueId(), fields_range: range }
      }
      return { ...field, uniqueId: generateUniqueId() }
    });
    setFieldsWithIds(fields);

  }, [data.fields]);

  useEffect(() => {
    const initialRangeValues = {};
    data.fields.forEach(field => {
      if (field.type === 'range') {
        initialRangeValues[field.uniqueId] = initialGlobalFilter[field.field];
      }
    });
    setRangeValues(initialRangeValues);
  }, [data.fields]);

  const updateGlobalFilter = useCallback((fieldName, value) => {
    setGlobalFilter(prev => ({
      ...prev,
      [fieldName]: value
    }));
    setPage(1); // Reset to first page when filter changes
    setProducts([]); // Clear current products
  }, []);

  const handleSortingChange = (event) => {
    setSorting(event.target.value);
    setPage(1); // Reset to first page when sorting changes
    setProducts([]); // Clear current products
  };

  const fetchProducts = useCallback(async () => {
    if (loading) return; // Avoid multiple fetches at the same time
    setLoading(true);

    try {
      const formData = new URLSearchParams();
      formData.append('action', 'get_products');
      formData.append('page', page);
      formData.append('per_page', 10);
      formData.append('product_type', productType);
      formData.append('display_fields', encodeURIComponent(JSON.stringify(displayFields)));
      formData.append('sorting', sorting); // Include sorting attribute
      Object.keys(globalFilter).forEach(key => {
        const data = JSON.stringify({
          values: globalFilter[key]
        });
        formData.append('filter_' + key, encodeURIComponent(data));
      });

      const response = await fetch('/wp-admin/admin-ajax.php?action=get_products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Http-Accept': 'application/json',
        },
        body: formData.toString(),
      });
      const data = await response.json();
      setProducts(prev => page === 1 ? data.products : [...prev, ...(data.products)]);
      setHasMore(data.products.length > 0); // If no products were returned, we've reached the end
      setProductCount(data.total);
    } catch (error) {
      console.error('Error fetching data:', error);
    }

    setLoading(false);
  }, [globalFilter, page, sorting]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleScroll = () => {
    if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) {
      if (hasMore) setPage(prev => prev + 1);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore]);

  const renderField = field => {
    switch (field.type) {
      case 'features':
        const featuresInitialOptions = globalFilter[field.field]
          ? field.fields_features
            .map((feature, index) => globalFilter[field.field].includes(feature.field) ? index : null)
            .filter(index => index !== null)
          : [];
        return (
          <FeaturesField
            key={field.uniqueId}
            field={field}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            updateGlobalFilter={updateGlobalFilter}
            initialSelectedOptions={featuresInitialOptions}
          />
        );
      case 'checkboxes':
        const checkboxesInitialOptions = globalFilter[field.field]
          ? field.fields_checkboxes
            .map((checkbox, index) => globalFilter[field.field].includes(checkbox.title) ? index : null)
            .filter(index => index !== null)
          : [];
        return (
          <CheckboxField
            key={field.uniqueId}
            field={field}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            updateGlobalFilter={updateGlobalFilter}
            initialSelectedOptions={checkboxesInitialOptions}
          />
        );
      case 'range':
        return (
          <RangeField
            key={field.uniqueId}
            field={field}
            rangeValues={rangeValues}
            setRangeValues={setRangeValues}
            updateGlobalFilter={updateGlobalFilter}
            initialRangeValues={initialRangeValues}
          />
        );
      case 'icons':
        const iconsInitialOptions = globalFilter[field.field]
          ? field.fields_icons
            .map((icon, index) => globalFilter[field.field].includes(icon.title) ? index : null)
            .filter(index => index !== null)
          : [];
        return (
          <IconsField
            key={field.uniqueId}
            field={field}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            updateGlobalFilter={updateGlobalFilter}
            initialSelectedOptions={iconsInitialOptions}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="contain pb-8 grid grid-cols-1 gap-6 lg:grid-cols-12 mt-8 lg:mt-10">
      {false && <h1 className="text-3xl mb-6 font-bold text-center md:text-5xl md:mb-10">{data.title}</h1>}
      <div className={`sidebar ${filtersVisible ? 'block' : 'hidden'} mr-0 ml-auto w-full lg:block lg:col-span-4`}>
        <div className={`p-6 w-full rounded-xl shadow-xl mx-auto lg:mx-0 lg:block lg:max-w-xs`}>
          <h3 className="text-xl font-bold mb-2">Select All That Apply</h3>
          <hr className="border border-cr-gray mb-4" />
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 lg:block">
            {fieldsWithIds.map(renderField)}
          </div>
        </div>
      </div>
      <main className="md:col-span-2 lg:col-span-8">
        {(loading && products.length === 0) ? (
          <p className="text-center text-xl pt-4">Loading...</p>
        ) : (
          <>
            {products.length > 0 ? (
              <>
                <div className="flex flex-col md:flex-row w-full justify-between mb-6">
                  <div className="font-bold text-xl text-slate-600 flex items-center mb-4 md:mb-0">
                    <span className="mr-2 text-cr-selection text-base md:text-xl">Showing {products.length} from {productCount} items</span>
                  </div>
                  <hr className="border border-cr-gray mb-4 md:hidden" />
                  <div class="flex justify-between">
                    <div className="lg:hidden mr-4">
                      <button
                        className="btn"
                        onClick={() => setFiltersVisible(!filtersVisible)}
                      >
                        {filtersVisible ? 'Hide Filters' : 'Show Filters'}
                      </button>
                    </div>
                    <div className="flex justify-center md:justify-start">
                      <span className="font-bold text-xl mr-2 hidden lg:flex lg:items-center">Sort by:</span>
                      <select className="sorting bg-slate-200 p-2 border-4 border-slate-200 rounded-lg" value={sorting} onChange={handleSortingChange}>
                        <option value="our-top-picks">Our Top Picks</option>
                        <option value="newest">Newest</option>
                        <option value="lowest-price">Lowest Price</option>
                        <option value="highest-price">Highest Price</option>
                      </select>
                    </div>
                  </div>
                </div>
                <table className="w-full border rounded-lg overflow-hidden border-subtle text-xl mb-8 flex flex-col xl:table">
                  <thead className="bg-primary/50 rounded">
                    <tr className='hidden text-2xl lg:table-row'>
                      <th className="p-4 py-8 text-left">Product</th>
                      <th className="p-4 py-8 text-left">Link</th>
                    </tr>
                  </thead>
                  <thead className="bg-primary/50 rounded">
                    <tr className="flex text-2xl lg:hidden">
                      <th className="p-4 py-6 md:py-8 text-center w-full">Product and Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <DatabaseProduct key={product.id} product={product} />
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="text-center text-xl pt-4">No products found</p>
            )}
          </>
        )}
        {loading && products.length > 0 && <p className="text-center text-cr-selection text-xl p-4">Loading...</p>}
      </main>
    </div>
  );

};

export default DatabaseComponent;
